import { useTheme } from 'styled-components';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import Column from '@sdk/components/ui/spacing/Column';
import ConfirmModal from '@sdk/components/ui/confirmModal/ConfirmModal';
import Button from '@sdk/components/ui/buttons/Button';
import StyledSvg from '@sdk/components/ui/StyledSvg';
import gettext from '@sdk/lang/gettext';
import useAuth from '@sdk/authentication/useAuth';
import HorizontalSeparator from '@sdk/components/ui/HorizontalSeparator';
import getActualClockInTimes from '@sdk/timeClock/utils/getActualClockInTimes';
import { BreakCategory } from '@sdk/settings/types';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { getEstimatedBreaksDuration } from '@sdk/timesheets/helpers/getEstimatedBreaksDuration';
import TextareaAutoheight from '@sdk/components/forms/TextareaAutoheight';

import { ButtonContainer, ModalContainer } from '@web/timeClock/ClockOutModal/clockOutModal.styles';
import { useClockOutModal } from '@web/timeClock/ClockOutModal/clockOutModal.hooks';
import ClockedInBanner from '@web/timeClock/ClockedInBanner/ClockedInBanner';
import ScheduledRow from '@web/timeClock/modals/rows/ScheduledRow/ScheduledRow';
import ActualRow from '@web/timeClock/modals/rows/ActualRow/ActualRow';
import DateRow from '@web/timeClock/modals/rows/DateRow/DateRow';
import LocationRow from '@web/timeClock/modals/rows/LocationRow/LocationRow';
import PositionRow from '@web/timeClock/modals/rows/PositionRow/PositionRow';
import BreakAttestationsModal from '@web/automatedBreaks/breakAttestations/BreakAttestationsModal/BreakAttestationsModal';
import TasksRow from '@web/timeClock/modals/rows/TasksRow/TasksRow';
import { useAutomatedBreaksSettings } from '@web/settings/components/schedule/Breaks/breaks.hooks';
import { Props as CommonProps } from '@web/timeClock/ClockOutModal/clockOutModal.types';
import TimeIconSvg from '@web/assets/svg/Icons/32/time-gray.svg';
import useIsAutomatedBreaksFeatureFlagEnabled from '@web/automatedBreaks/hooks/useIsAutomatedBreaksFeatureFlagEnabled';
import CommentIconSvg from '@web/assets/svg/Icons/32/clock-note-icon.svg';
import ModalRow from '@web/timeClock/modals/rows/ModalRow/ModalRow';
import { AddCommentColumn } from '@web/timeClock/modals/rows/CommentWithEditModeRow/commentWithEditModeRow.styles';

type Props = CommonProps & {
  onTaskPreview: (props: { taskInstance: any }) => void;
  isTaskModalVisible: boolean;
};

const ClockOutModal = ({
  shift,
  onCommentSave,
  clockOut,
  onTaskPreview,
  isTaskModalVisible,
}: Props) => {
  const theme = useTheme();
  const {
    isVisible,
    openClockOutModal,
    dismissClockOutModal,
    missedBreaks,
    acceptAttestations,
    declineAttestations,
    isAttestationVisible,
    save,
    commentField,
    onCommentChange,
    shiftWithDetails,
  } = useClockOutModal({ shift, onCommentSave, clockOut });
  const { currentUser } = useAuth();
  const { automatedBreaks } = useAutomatedBreaksSettings();
  const automatedBreaksFeatureEnabled = useIsAutomatedBreaksFeatureFlagEnabled();
  const { worldClockEnabled } = currentUser.settings;
  const hasTasks = Boolean(shiftWithDetails?.taskInstances?.length);
  const clockInTimes = getActualClockInTimes({
    automatedBreaksFeatureEnabled,
    automatedBreaksSettings: automatedBreaks,
    areStandardBreaksPaid: currentUser.orgSettings.shiftBreakType === BreakCategory.paid,
    worldClockEnabled,
    timezone: shift.location.timezone,
    timesheetEntries: shift.timesheetEntries,
    timesheetProjections: shift.timesheetProjections,
    scheduledBreaksDuration: getEstimatedBreaksDuration({
      shift,
      automatedBreaksSettings: automatedBreaks,
      orgSettings: currentUser.orgSettings,
    }),
  });
  const hasAnyTimesheet = Boolean(clockInTimes.length);

  return (
    <>
      <ButtonContainer mLeft={10}>
        <Button onClick={openClockOutModal}>{gettext('Clock out')}</Button>
      </ButtonContainer>
      {isVisible && !isTaskModalVisible && Boolean(shiftWithDetails) && (
        <ConfirmModal
          fullScreen
          bodyPadding={0}
          buttonsMargin={0}
          titleText={gettext('Time clock · %(userName)s', { userName: shift.user.fullname })}
          titleIcon={<StyledSvg svg={TimeIconSvg} color={theme.colors[ThemeColorType.primary]} />}
          titleThemeColor={ThemeColorType.primary}
          primaryButtonText={gettext('Clock out')}
          primaryButtonType={ButtonType.success}
          secondaryButtonText={gettext('Cancel')}
          dismiss={dismissClockOutModal}
          onPrimaryClick={save}
          dismissOnBackdropClick={false}>
          <ModalContainer>
            <ClockedInBanner clockedIn={shift.clockedIn} />
            <Column pRight={20} pLeft={20}>
              <DateRow date={shift.dtstart} />
              <HorizontalSeparator themeColor={ThemeColorType.separatorOnGray} />
              <LocationRow location={shift.location} />
              <HorizontalSeparator themeColor={ThemeColorType.separatorOnGray} />
              <PositionRow position={shift.position} />
              <HorizontalSeparator themeColor={ThemeColorType.separatorOnGray} />
              {hasTasks && (
                <>
                  <TasksRow
                    taskInstances={shiftWithDetails.taskInstances}
                    onTaskPreview={onTaskPreview}
                  />
                  <HorizontalSeparator themeColor={ThemeColorType.separatorOnGray} />
                </>
              )}
              <ScheduledRow shift={shift} />
              <HorizontalSeparator themeColor={ThemeColorType.separatorOnGray} />
              {hasAnyTimesheet && (
                <>
                  <ActualRow shift={shift} clockInTimes={clockInTimes} />
                  <HorizontalSeparator themeColor={ThemeColorType.separatorOnGray} />
                </>
              )}
              <ModalRow label={gettext('Comment')} icon={CommentIconSvg}>
                <AddCommentColumn>
                  <TextareaAutoheight
                    value={commentField.value}
                    onChange={onCommentChange}
                    placeholder={gettext('Add a comment')}
                  />
                </AddCommentColumn>
              </ModalRow>
              <HorizontalSeparator themeColor={ThemeColorType.separatorOnGray} />
            </Column>
          </ModalContainer>
        </ConfirmModal>
      )}
      <BreakAttestationsModal
        isVisible={isAttestationVisible}
        accept={acceptAttestations}
        decline={declineAttestations}
        missedBreaks={missedBreaks}
        orgSettings={currentUser.orgSettings}
        shift={shift}
      />
    </>
  );
};

export default ClockOutModal;
